import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const CaseStudySection = () => {
  const caseStudy = {
    title: 'PlaCare',
    subtitle: 'Plant Care Made Simple',
    role: 'UX Design',
    platform: 'Mobile App End-to-End',
    tagline: 'A mobile app that takes the guesswork out of keeping plants alive, built for people who love plants but don\'t always know how to care for them.',
    overview:
      'PlaCare is a mobile app designed to help plant owners identify, care for, and manage their plants through clear, guided actions. Rather than overwhelming users with encyclopedic plant knowledge, it delivers the right information at the right time - making healthy plant care feel effortless.',
    goal: 'Reduce the friction between wanting to care for your plants and actually knowing how.',
    problemTitle: 'Plants suffer when their owners feel lost',
    problem:
      'Most plant owners - especially beginners - don\'t lack enthusiasm. They lack clear, timely guidance. They forget watering schedules, get buried in conflicting advice online, and can\'t identify what their plant actually needs. The result: wilted plants and frustrated owners who give up entirely.',
    research: [
      'Users regularly miss watering and fertilizing windows',
      'Existing apps overwhelm users with too much information at once',
      'People prefer quick, actionable steps over long reads',
      'Community features motivate users to stay consistent',
    ],
    personas: [
      {
        initials: 'EM',
        name: 'Emma',
        type: 'Busy professional',
        needs: [
          'Limited time for plant care',
          'Needs quick reminders, not reading material',
          'Loses interest when an app asks too much of her',
        ],
      },
      {
        initials: 'NP',
        name: 'New plant owner',
        type: 'Beginner',
        needs: [
          'Lacks foundational plant knowledge',
          'Unsure how to build a care routine',
          'Needs step-by-step guidance, not jargon',
        ],
      },
    ],
    problemStatement:
      'Busy and beginner plant owners need a simple, reliable way to care for their plants without feeling overwhelmed - so they can build consistent habits and keep their plants healthy with minimal effort.',
    designProcess: {
      ia: 'Before any screens were designed, the app\'s structure was mapped end-to-end - from onboarding new users to managing plants and tracking recurring tasks. This ensured navigation would feel intuitive without requiring users to think about where to go next.',
      paperWireframes: 'Low-fidelity sketches came first. Rough paper wireframes let ideas be tested and discarded quickly - no polish, just flow. Key screens covered onboarding, plant identification, task management, and community features.',
      lofi: 'Digital lo-fi prototypes translated the sketches into testable flows, validating the information architecture before any visual design was applied. Screens included the full user journey: welcome, plant search, scan view, plant details, task management, and reminders.',
      solution: 'The final interface reduces cognitive load at every turn. A streamlined onboarding flow gets users set up in minutes. Camera-based plant identification removes the guesswork entirely. Care instructions are surfaced just-in-time - shown when relevant, hidden when not.',
      solutionQuestion: 'Every design decision asked the same question: does this make caring for plants easier, or harder?',
    },
    features: [
      {
        title: 'Plant identification',
        description: 'Point and scan - the camera instantly identifies a plant and pulls up its care profile.',
      },
      {
        title: 'Care reminders',
        description: 'Automated watering and fertilizing schedules with smart nudges so nothing gets missed.',
      },
      {
        title: 'Personalised guidance',
        description: 'Care tips tailored to plant type, environment, and user preferences - not a one-size-fits-all manual.',
      },
    ],
  };

  return (
    <section id="work" className="py-24 px-6 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="mb-16">
          <h2
            className="text-5xl md:text-6xl font-bold mb-4 text-gray-900"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Featured Work
          </h2>
          <p className="text-[#EA8649] text-lg font-semibold">Case Study</p>
        </div>

        {/* Case Study Content */}
        <div className="space-y-16">
          {/* Header */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {caseStudy.title}
              </h3>
            </div>
            <p className="text-2xl text-gray-600 mb-4">{caseStudy.subtitle}</p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">{caseStudy.tagline}</p>
            <div className="flex flex-wrap gap-3">
              <Badge variant="secondary" className="text-sm px-4 py-2 bg-[#EA8649] text-white border-0">
                {caseStudy.role}
              </Badge>
              <Badge variant="secondary" className="text-sm px-4 py-2 bg-[#5A6C73] text-white border-0">
                {caseStudy.platform}
              </Badge>
            </div>
          </div>

          {/* Overview */}
          <Card className="p-8 md:p-12 border-0 shadow-md bg-white">
            <h4 className="text-2xl font-bold mb-4 text-[#EA8649]">Overview</h4>
            <p className="text-gray-700 text-lg leading-relaxed">{caseStudy.overview}</p>
            <div className="mt-6 p-4 bg-orange-50 rounded-lg border-l-4 border-[#EA8649]">
              <span className="font-semibold text-gray-900">Goal: </span>
              <span className="text-gray-700">{caseStudy.goal}</span>
            </div>
          </Card>

          {/* Problem */}
          <div>
            <h4 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              The Problem
            </h4>
            <Card className="p-8 md:p-12 border-0 shadow-md bg-white border-l-4 border-l-[#EA8649]">
              <h5 className="text-xl font-bold mb-4 text-gray-900">{caseStudy.problemTitle}</h5>
              <p className="text-gray-700 text-lg leading-relaxed">{caseStudy.problem}</p>
            </Card>
          </div>

          {/* Competitive Audit */}
          <div>
            <h4 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Competitive Audit
            </h4>
            <p className="text-gray-700 text-lg mb-8 leading-relaxed">
              I conducted a competitive audit to identify strengths and weaknesses of existing plant care apps. This analysis helped understand market standards, discover opportunities for differentiation, and ensure PlaCare offers a superior user experience.
            </p>
            
            {/* Competitive Audit Table Image */}
            <Card className="p-8 border-0 shadow-md bg-white mb-8">
              <div className="w-full rounded-lg overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/j8xclesa_Competitive%20Audit_page-0001.jpg"
                  alt="Competitive Audit Comparison Table"
                  className="w-full h-auto"
                />
              </div>
            </Card>

            {/* Summary */}
            <Card className="p-8 border-0 shadow-md bg-gradient-to-br from-orange-50 to-white border-l-4 border-l-[#EA8649]">
              <h5 className="text-xl font-bold mb-4 text-gray-900">Key Findings</h5>
              <div className="space-y-4">
                <div>
                  <h6 className="font-semibold text-[#EA8649] mb-2">✓ Strengths (Common Across Competitors)</h6>
                  <ul className="space-y-2 ml-4">
                    <li className="text-gray-700 flex items-start gap-2">
                      <span className="text-[#EA8649] mt-1">•</span>
                      <span>Strong plant identification features with varying accuracy levels</span>
                    </li>
                    <li className="text-gray-700 flex items-start gap-2">
                      <span className="text-[#EA8649] mt-1">•</span>
                      <span>Care reminders and tracking capabilities for watering and fertilizing</span>
                    </li>
                    <li className="text-gray-700 flex items-start gap-2">
                      <span className="text-[#EA8649] mt-1">•</span>
                      <span>Multi-device accessibility and user-friendly interfaces</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h6 className="font-semibold text-gray-900 mb-2">⚠ Opportunities (Where Competitors Fall Short)</h6>
                  <ul className="space-y-2 ml-4">
                    <li className="text-gray-700 flex items-start gap-2">
                      <span className="text-gray-600 mt-1">•</span>
                      <span><strong>Complex navigation:</strong> Planta's feature-rich interface can be overwhelming for beginners</span>
                    </li>
                    <li className="text-gray-700 flex items-start gap-2">
                      <span className="text-gray-600 mt-1">•</span>
                      <span><strong>Steep learning curve:</strong> Most apps assume prior plant knowledge</span>
                    </li>
                    <li className="text-gray-700 flex items-start gap-2">
                      <span className="text-gray-600 mt-1">•</span>
                      <span><strong>Limited beginner guidance:</strong> Apps focus on features rather than education and confidence-building</span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>

          {/* Research */}
          <div>
            <h4 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Research Insights
            </h4>
            <div className="grid md:grid-cols-2 gap-6">
              {caseStudy.research.map((insight, index) => (
                <Card key={index} className="p-6 border-0 shadow-md bg-white hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#EA8649] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {index + 1}
                    </div>
                    <p className="text-gray-700">{insight}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* User Personas */}
          <div>
            <h4 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              User Personas
            </h4>
            <div className="grid md:grid-cols-2 gap-8">
              {caseStudy.personas.map((persona, index) => (
                <Card key={index} className="p-8 border-0 shadow-md bg-white">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-[#EA8649] text-white flex items-center justify-center text-xl font-bold flex-shrink-0">
                      {persona.initials}
                    </div>
                    <div>
                      <h5 className="text-xl font-bold text-[#EA8649]">{persona.name}</h5>
                      <p className="text-gray-600">{persona.type}</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {persona.needs.map((need, idx) => (
                      <li key={idx} className="text-gray-700 flex items-start gap-2">
                        <span className="text-[#EA8649] mt-1">•</span>
                        <span>{need}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>

          {/* Affinity Diagram */}
          <div>
            <h4 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Affinity Diagram
            </h4>
            <p className="text-gray-700 text-lg mb-8 leading-relaxed">
              Brainstormed and organized information from user research to identify key themes and opportunities across six major categories.
            </p>
            <Card className="p-8 border-0 shadow-md bg-white">
              <div className="w-full rounded-lg overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/1uckrdwu_ChatGPT%20Image%20Apr%2021%2C%202026%2C%2001_18_14%20PM.png"
                  alt="Affinity Diagram showing user research insights organized into categories"
                  className="w-full h-auto"
                />
              </div>
              <div className="mt-6 grid md:grid-cols-3 gap-4 text-sm text-gray-700">
                <div>
                  <h6 className="font-semibold text-[#EA8649] mb-2">Key Categories:</h6>
                  <ul className="space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-[#EA8649]">•</span>
                      <span>Care & Knowledge Gaps</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#EA8649]">•</span>
                      <span>Task & Reminder Needs</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h6 className="font-semibold text-[#EA8649] mb-2">User Challenges:</h6>
                  <ul className="space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-[#EA8649]">•</span>
                      <span>Cognitive Overload</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#EA8649]">•</span>
                      <span>Plant Identification & Onboarding</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h6 className="font-semibold text-[#EA8649] mb-2">Opportunities:</h6>
                  <ul className="space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-[#EA8649]">•</span>
                      <span>Problem Solving (Plant Health)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#EA8649]">•</span>
                      <span>Motivation & Engagement</span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>

          {/* Problem Statement */}
          <div>
            <h4 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Problem Statement
            </h4>
            <Card className="p-8 md:p-12 border-l-4 border-l-[#EA8649] border-t-0 border-r-0 border-b-0 shadow-md bg-gradient-to-r from-orange-50 to-white">
              <p className="text-gray-900 text-lg md:text-xl leading-relaxed font-medium">
                {caseStudy.problemStatement}
              </p>
            </Card>
          </div>

          {/* Pain Points & Solutions */}
          <div>
            <h4 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Pain Points & Solutions
            </h4>
            <p className="text-gray-700 text-lg mb-8 leading-relaxed">
              Based on user research and affinity mapping, I identified seven key pain points and designed targeted solutions to address each challenge.
            </p>
            <Card className="p-8 border-0 shadow-md bg-white">
              <div className="w-full rounded-lg overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/481ayz65_ChatGPT%20Image%20Apr%2021%2C%202026%2C%2001_21_12%20PM.png"
                  alt="Pain Points and Solutions mapping"
                  className="w-full h-auto"
                />
              </div>
              <div className="mt-6 grid md:grid-cols-2 gap-6">
                <div>
                  <h6 className="font-semibold text-gray-900 mb-3">🔴 Key Pain Points</h6>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">1.</span>
                      <span><strong>Lack of Plant Care Knowledge</strong> - Users don't know watering frequency, sunlight needs, or soil requirements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">2.</span>
                      <span><strong>Forgetting Plant Care Tasks</strong> - Inconsistent care due to forgotten watering and fertilizing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">3.</span>
                      <span><strong>Overwhelming & Complex Apps</strong> - Too many features and complicated navigation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">4.</span>
                      <span><strong>Difficulty in Identifying Plants</strong> - Users struggle with plant names and identification</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h6 className="font-semibold text-gray-900 mb-3">✅ Designed Solutions</h6>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">→</span>
                      <span><strong>Simplified Care Information</strong> - Clear parameters with easy-to-understand visuals</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">→</span>
                      <span><strong>Task Management & Reminders</strong> - Today/Upcoming tasks with smart notifications</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">→</span>
                      <span><strong>Simple & Focused UX</strong> - Clean, minimal interface with clear information architecture</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">→</span>
                      <span><strong>Scan-Based Plant Identification</strong> - Instant plant details with one-tap add to My Plants</span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>

          {/* Information Architecture */}
          <div>
            <h4 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Information Architecture
            </h4>
            <p className="text-gray-700 text-lg mb-8 leading-relaxed">
              {caseStudy.designProcess.ia}
            </p>
            
            <div className="space-y-8">
              {/* New User Flow */}
              <Card className="p-8 border-0 shadow-md bg-white">
                <h5 className="text-xl font-bold mb-4 text-[#EA8649]">User Flow - Add a New Plant</h5>
                <p className="text-gray-600 mb-6">
                  Complete user journey showing steps from welcome to adding a plant and setting reminders.
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/y9aicx2o_ChatGPT%20Image%20Apr%2021%2C%202026%2C%2001_34_15%20PM.png"
                    alt="User Flow showing the complete journey to add a new plant"
                    className="w-full h-auto rounded-lg"
                  />
                </div>
              </Card>

              {/* Overall Platform Architecture */}
              <Card className="p-8 border-0 shadow-md bg-white">
                <h5 className="text-xl font-bold mb-4 text-[#EA8649]">Overall Platform Architecture</h5>
                <p className="text-gray-600 mb-6">
                  Complete navigation structure showing all main sections: Home, My Plants, Tasks, Community, and Profile management.
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/dwe8p47m_ChatGPT%20Image%20Apr%2021%2C%202026%2C%2001_34_15%20PM%20-%20Copy.png"
                    alt="Overall Information Architecture showing app hierarchy"
                    className="w-full h-auto rounded-lg"
                  />
                </div>
              </Card>
            </div>
          </div>

          {/* Paper Wireframes */}
          <div>
            <h4 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Paper Wireframes
            </h4>
            <p className="text-gray-700 text-lg mb-8 leading-relaxed">
              {caseStudy.designProcess.paperWireframes}
            </p>
            <Card className="p-8 border-0 shadow-md bg-white">
              <div className="w-full rounded-lg overflow-hidden bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/rfpvh14s_Paper%20Wireframe%20%281%29_page-0001.jpg"
                  alt="Paper Wireframes"
                  className="w-full h-auto"
                />
              </div>
              <div className="mt-6 grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Key Screens Sketched:</h5>
                  <ul className="space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-[#EA8649]">•</span>
                      <span>Onboarding & Welcome</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#EA8649]">•</span>
                      <span>Set Preferences</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#EA8649]">•</span>
                      <span>Plant Identification & Scanning</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#EA8649]">•</span>
                      <span>Plant Details View</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Additional Features:</h5>
                  <ul className="space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-[#EA8649]">•</span>
                      <span>Task Log & Reminders</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#EA8649]">•</span>
                      <span>Community Feed</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#EA8649]">•</span>
                      <span>Marketplace</span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>

          {/* LoFi Prototypes */}
          <div>
            <h4 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Low-Fidelity Prototypes
            </h4>
            <p className="text-gray-700 text-lg mb-8 leading-relaxed">
              {caseStudy.designProcess.lofi}
            </p>
            <Card className="p-8 border-0 shadow-md bg-white">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
                {[
                  { url: 'https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/yoj4lx3j_11%20%282%29.png', label: 'Welcome' },
                  { url: 'https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/ndrh5pjs_22.png', label: 'Onboarding' },
                  { url: 'https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/pjxw7oaa_33.png', label: 'Plant Identification' },
                  { url: 'https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/f0mxdfts_44.png', label: 'Camera Scan' },
                  { url: 'https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/7mv4wm3u_55.png', label: 'Plant Details' },
                  { url: 'https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/kfkhakpn_66.png', label: 'Plant Added' },
                  { url: 'https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/8lwwf93h_77.png', label: 'Plant Preferences' },
                  { url: 'https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/ta52qr2v_88.png', label: 'Set Reminder' },
                  { url: 'https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/s9kje97j_99.png', label: 'Reminder Added' },
                  { url: 'https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/zoyck0xi_101.png', label: 'Task To Do' },
                  { url: 'https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/418gjgad_102.png', label: 'Your Plants' },
                ].map((screen, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div className="w-full max-w-[180px]">
                      <img 
                        src={screen.url} 
                        alt={screen.label}
                        className="w-full h-auto"
                      />
                    </div>
                    <p className="text-center text-sm text-gray-600 mt-4 font-medium">{screen.label}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* HiFi Prototype */}
          <div>
            <h4 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              HiFi Prototype
            </h4>
            <p className="text-gray-700 text-lg mb-4 leading-relaxed">
              {caseStudy.designProcess.solution}
            </p>
            <p className="text-gray-900 text-lg mb-8 leading-relaxed font-medium italic">
              {caseStudy.designProcess.solutionQuestion}
            </p>
            
            {/* App Mockups Grid with iPhone Frames */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {/* First 6 screens - No frames (user provided with frames) */}
              <div className="flex flex-col items-center">
                <div className="w-full max-w-[240px]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/c6oj4qbm_01-A.png"
                    alt="Welcome Screen"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-4 font-medium">Welcome Screen</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-full max-w-[240px]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/uca1mvup_02-A.png"
                    alt="Onboarding"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-4 font-medium">Onboarding</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-full max-w-[240px]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/om9mte4t_03-A.png"
                    alt="Plant ID"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-4 font-medium">Plant ID</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-full max-w-[240px]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/h3gf0ljj_04-A.png"
                    alt="Camera Scan"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-4 font-medium">Camera Scan</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-full max-w-[240px]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/yvrjdg4w_05-A.png"
                    alt="Plant Details"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-4 font-medium">Plant Details</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-full max-w-[240px]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/pofz6unl_06-A.png"
                    alt="Added to Plants"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-4 font-medium">Added to Plants</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-full max-w-[350px]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/7h7czner_download.gif"
                    alt="Personalize Care"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-4 font-medium">Personalize Care</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-full max-w-[240px]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/ccmhqava_08-A.png"
                    alt="Set Reminder"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-4 font-medium">Set Reminder</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-full max-w-[240px]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/wgp7u4lu_09-A.png"
                    alt="Added Reminder"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-4 font-medium">Added Reminder</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-full max-w-[240px]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/togi5rxt_10-A.png"
                    alt="Tasks"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-4 font-medium">Tasks</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-full max-w-[240px]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/mvuj2fqz_11-A.png"
                    alt="My Plants"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-4 font-medium">My Plants</p>
              </div>
            </div>
          </div>

          {/* Interactive Prototype */}
          <div>
            <h5 className="text-2xl font-bold mb-6 text-[#EA8649]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Interactive Prototype
            </h5>
            <p className="text-gray-700 text-lg mb-8 leading-relaxed">
              Experience the complete user flow through this interactive prototype demonstration, showcasing the onboarding process and key features of PlaCare.
            </p>
            <Card className="p-8 border-0 shadow-md bg-white">
              <div className="flex justify-center">
                <div className="w-full max-w-[300px]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plant-care-guide-12/artifacts/sdwur32t_download%20%282%29.gif"
                    alt="Interactive Prototype Demo"
                    className="w-full h-auto rounded-lg"
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* Key Features */}
          <div>
            <h4 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Key Features
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              {caseStudy.features.map((feature, index) => (
                <Card key={index} className="p-6 border-0 shadow-md bg-white hover:shadow-lg transition-shadow">
                  <h5 className="text-lg font-bold mb-3 text-[#EA8649]">{feature.title}</h5>
                  <p className="text-gray-700">
                    {feature.description}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudySection;
